import { useEffect, useState } from "react";

/** Install and update prompts for the sideloaded Android app.
 *
 *  There is no store, so nothing tells the user the app exists or that a newer
 *  build is out. Two prompts fill that gap:
 *
 *   - Opened in a **phone browser** with the app not installed → offer to
 *     install it.
 *   - Running **inside the app** on an older build → offer the update.
 *
 *  The hard part is that a Trusted Web Activity gives the page nothing to
 *  detect it by. Same origin, same Chrome, same user agent — from JavaScript
 *  it is indistinguishable from a browser tab. So the Android build generates
 *  its launch URL with the installed versionCode baked in
 *  (`?wovApp=3`, see the app's build.gradle.kts), and this reads it. Generated
 *  from versionCode rather than hand-written, because a stale marker is worse
 *  than none: it would nag about an update that is already installed. */

const MARKER_PARAM = "wovApp";
const SESSION_KEY = "wov:app-version";
const SNOOZE_KEY = "wov:install-snooze";

/** An update is worth interrupting for again the next day. An install prompt
 *  for something you have declined is not — a week. */
const SNOOZE_MS = { update: 24 * 3600_000, install: 7 * 24 * 3600_000 };

export type AppPrompt = "install" | "update" | "none";

export type LatestApp = { versionCode: number; versionName: string; apkUrl: string; notes?: string };

/** Read the marker, remember it, and take it out of the address bar.
 *
 *  Session storage rather than a variable: the TWA only puts the marker on the
 *  *launch* URL, so an in-app navigation to /calendar would otherwise look
 *  like a browser tab and start advertising the install.
 *
 *  Exported and called at module load so the marker is captured before any
 *  router has a chance to rewrite the URL. */
export function captureAppMarker(): void {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  const raw = url.searchParams.get(MARKER_PARAM);
  if (raw == null) return;
  const code = Number.parseInt(raw, 10);
  try {
    if (Number.isFinite(code)) sessionStorage.setItem(SESSION_KEY, String(code));
  } catch {
    /* private mode */
  }
  // Leave the address bar clean; the marker has served its purpose.
  url.searchParams.delete(MARKER_PARAM);
  window.history.replaceState(window.history.state, "", url.toString());
}

/** The installed versionCode, or null when this is an ordinary browser. */
export function installedVersionCode(): number | null {
  try {
    const v = sessionStorage.getItem(SESSION_KEY);
    if (v == null) return null;
    const n = Number.parseInt(v, 10);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

/** Which prompt, if any. Pure, so the rules are assertable.
 *
 *  Deliberately conservative in both directions: never advertise an install on
 *  a desktop, where the APK is useless, and never claim an update without a
 *  version to compare against — a missing or malformed feed must produce
 *  silence, not a banner that cannot be satisfied. */
export function decidePrompt(opts: {
  installed: number | null;
  latest: LatestApp | null;
  isMobile: boolean;
  /** True when running in an installed app rather than a browser tab. A TWA
   *  always reports standalone display mode, so this identifies the app even
   *  on builds predating the version marker. */
  standalone: boolean;
  /** Timestamp of the last dismissal, per kind. */
  snoozedUntil: number;
  now: number;
}): AppPrompt {
  const { installed, latest, isMobile, standalone, snoozedUntil, now } = opts;
  if (now < snoozedUntil) return "none";
  if (installed != null) {
    if (!latest || !Number.isFinite(latest.versionCode)) return "none";
    return latest.versionCode > installed ? "update" : "none";
  }
  // Installed, but from a build that predates the version marker. We know it
  // is the app and nothing more, so say nothing: offering the install would be
  // wrong, and claiming an update without a version to compare would be a
  // guess. The next update carries a marker and this resolves itself.
  if (standalone) return "none";
  // A browser. Only worth offering on a device that can run the APK.
  if (!isMobile) return "none";
  return latest ? "install" : "none";
}

/** True in an installed PWA or a Trusted Web Activity. */
export function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches === true ||
    // The TWA launches via an Android intent, which shows up here. Belt and
    // braces: display-mode has been the reliable one in testing, but this
    // costs nothing.
    document.referrer.startsWith("android-app://")
  );
}

function readSnooze(kind: "install" | "update"): number {
  try {
    return Number.parseInt(localStorage.getItem(`${SNOOZE_KEY}:${kind}`) ?? "0", 10) || 0;
  } catch {
    return 0;
  }
}

export function snooze(kind: "install" | "update"): void {
  try {
    localStorage.setItem(`${SNOOZE_KEY}:${kind}`, String(Date.now() + SNOOZE_MS[kind]));
  } catch {
    /* private mode */
  }
}

export function useAppPrompt(isMobile: boolean): { prompt: AppPrompt; latest: LatestApp | null; dismiss: () => void } {
  const [latest, setLatest] = useState<LatestApp | null>(null);
  const [dismissed, setDismissed] = useState(0);

  useEffect(() => {
    let alive = true;
    // A static file on the same origin — no session, no bridge, no COM. Safe
    // to ask for on every load, and it must never block anything: a failure
    // here simply means no prompt.
    void fetch("/app/latest.json", { cache: "no-cache" })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => alive && setLatest(j && typeof j.versionCode === "number" ? (j as LatestApp) : null))
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, []);

  const installed = installedVersionCode();
  const kind = installed != null ? "update" : "install";
  const prompt = decidePrompt({
    installed,
    latest,
    isMobile,
    standalone: isStandalone(),
    snoozedUntil: Math.max(readSnooze(kind), dismissed),
    now: Date.now(),
  });

  return {
    prompt,
    latest,
    dismiss: () => {
      snooze(kind);
      // Local state as well as storage, so the banner goes immediately rather
      // than on the next render that happens to re-read localStorage.
      setDismissed(Date.now() + SNOOZE_MS[kind]);
    },
  };
}
